import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import HackCard from '@/components/HackCard';
import ProductCard from '@/components/ProductCard';
import ProductDetailDialog from '@/components/ProductDetailDialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Heart } from 'lucide-react';

export default function FavoritesPage() {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openProduct, setOpenProduct] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/favorites');
      setFavorites(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const hacks = favorites.filter((f) => f.item_type === 'hack').map((f) => f.item);
  const products = favorites.filter((f) => f.item_type === 'product').map((f) => f.item);

  return (
    <div data-testid="favorites-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold">Your wishlist</span>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-ink leading-[1.05]">
          My Favorites
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Everything you’ve loved, in one place. Tap any heart to remove.
        </p>
      </div>

      <div className="mt-8">
        <Tabs defaultValue="all" data-testid="favorites-tabs">
          <TabsList className="bg-secondary rounded-xl p-1 border border-border/70 grid grid-cols-3 max-w-md">
            <TabsTrigger value="all" className="rounded-lg data-[state=active]:bg-card">
              All ({favorites.length})
            </TabsTrigger>
            <TabsTrigger value="hacks" className="rounded-lg data-[state=active]:bg-card">
              Hacks ({hacks.length})
            </TabsTrigger>
            <TabsTrigger value="products" className="rounded-lg data-[state=active]:bg-card">
              Products ({products.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6" data-testid="favorites-grid">
            {loading ? (
              <Loading />
            ) : favorites.length === 0 ? (
              <Empty />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {hacks.map((h) => (
                  <HackCard key={`h-${h.id}`} hack={h} />
                ))}
                {products.map((p) => (
                  <ProductCard key={`p-${p.id}`} product={p} onOpen={setOpenProduct} />
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="hacks" className="mt-6">
            {loading ? (
              <Loading />
            ) : hacks.length === 0 ? (
              <Empty type="hacks" />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {hacks.map((h) => (
                  <HackCard key={h.id} hack={h} />
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="products" className="mt-6">
            {loading ? (
              <Loading />
            ) : products.length === 0 ? (
              <Empty type="products" />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {products.map((p) => (
                  <ProductCard key={p.id} product={p} onOpen={setOpenProduct} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <ProductDetailDialog
        product={openProduct}
        open={!!openProduct}
        onOpenChange={(o) => !o && setOpenProduct(null)}
      />
    </div>
  );
}

const Loading = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
    {[...Array(6)].map((_, i) => (
      <Skeleton key={i} className="h-80 rounded-2xl" />
    ))}
  </div>
);

const Empty = ({ type = 'items' }) => (
  <div className="text-center py-16" data-testid="page-empty-state">
    <div className="mx-auto h-14 w-14 rounded-full bg-accent/40 border border-rose-tint flex items-center justify-center">
      <Heart className="h-6 w-6 text-rose-gold" />
    </div>
    <p className="mt-4 font-display text-2xl text-ink">No {type} yet.</p>
    <p className="text-sm text-muted-foreground mt-1">Tap the heart on any hack or product to start your wishlist.</p>
    <div className="mt-5 flex items-center justify-center gap-2">
      <Button asChild variant="outline" className="rounded-xl">
        <Link to="/diy">Browse DIY Lab</Link>
      </Button>
      <Button asChild className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90">
        <Link to="/products">Open the Curator</Link>
      </Button>
    </div>
  </div>
);
